import React, { useState } from "react";
import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	IconButton,
	Typography,
	Link,
	Pagination,
	Collapse
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { AuditLogEntry } from "../../models/auditLog";
import { ChangeDetail } from "./ChangeDetail";

interface AuditLogTableProps {
	auditLogs: AuditLogEntry[];
}

const ROWS_PER_PAGE = 50;

export const AuditLogTable: React.FC<AuditLogTableProps> = ({ auditLogs }) => {
	const theme = useTheme();
	const isDarkMode = theme.palette.mode === "dark";
	const [page, setPage] = useState(1);
	const [expandedId, setExpandedId] = useState<string | null>(null);

	const pageCount = Math.max(1, Math.ceil(auditLogs.length / ROWS_PER_PAGE));
	const startIndex = (page - 1) * ROWS_PER_PAGE;
	const pagedItems: AuditLogEntry[] = auditLogs.slice(
		startIndex,
		startIndex + ROWS_PER_PAGE
	);
	const headerCellSx = {
		fontWeight: 800,
		textTransform: "uppercase",
		letterSpacing: "0.06em",
		fontSize: "0.85rem",
		color: "text.primary",
		borderBottom: `2px solid ${theme.palette.divider}`,
		position: "sticky" as const,
		top: 0,
		zIndex: 3,
		background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, isDarkMode ? 0.22 : 0.1)}, ${alpha(theme.palette.background.paper, 0.98)})`,
		py: 1
	};

	return (
		<Paper
			elevation={3}
			sx={{
				p: 2,
				mb: 3,
				bgcolor: theme.palette.background.paper,
				color: theme.palette.text.primary,
				border: `1px solid ${theme.palette.divider}`,
				borderRadius: 2
			}}
		>
			<Typography
				variant="h6"
				gutterBottom
				sx={{ color: "text.primary" }}
			>
				Audit History
			</Typography>
			<TableContainer sx={{ maxHeight: 520, borderRadius: 1 }}>
				<Table stickyHeader size="small">
					<TableHead>
						<TableRow
							sx={{
								background: alpha(
									theme.palette.primary.main,
									isDarkMode ? 0.16 : 0.08
								)
							}}
						>
							<TableCell
								sx={{
									width: 56,
									position: "sticky",
									left: 0,
									top: 0,
									zIndex: 3,
									background: alpha(
										theme.palette.background.paper,
										0.98
									),
									py: 1
								}}
							/>
							<TableCell sx={headerCellSx}>Timestamp</TableCell>
							<TableCell sx={headerCellSx}>Entity</TableCell>
							<TableCell sx={headerCellSx}>Invoice #</TableCell>
							<TableCell sx={headerCellSx}>Operation</TableCell>
							<TableCell sx={headerCellSx}>User</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{pagedItems.map((log: AuditLogEntry) => {
							const isExpanded = expandedId === log.id;
							return (
								<React.Fragment key={log.id}>
									<TableRow
										hover
										sx={{
											"&:hover": {
												backgroundColor: alpha(
													theme.palette.action.hover,
													isDarkMode ? 0.16 : 0.06
												)
											}
										}}
									>
										<TableCell
											sx={{
												py: 0.75,
												px: 1,
												borderBottom: `1px solid ${theme.palette.divider}`
											}}
										>
											<IconButton
												aria-label={
													isExpanded
														? "Collapse details"
														: "Expand details"
												}
												onClick={() =>
													setExpandedId(
														isExpanded
															? null
															: log.id
													)
												}
												size="small"
												sx={{ color: "text.secondary" }}
											>
												{isExpanded ? (
													<KeyboardArrowUpIcon />
												) : (
													<KeyboardArrowDownIcon />
												)}
											</IconButton>
										</TableCell>
										<TableCell
											sx={{
												py: 1,
												px: 2,
												color: "text.primary",
												borderBottom: `1px solid ${theme.palette.divider}`
											}}
										>
											{new Date(
												log.timestamp
											).toLocaleString()}
										</TableCell>
										<TableCell
											sx={{
												py: 1,
												px: 2,
												color: "text.primary",
												borderBottom: `1px solid ${theme.palette.divider}`
											}}
										>
											{log.entityType}
										</TableCell>
										<TableCell
											sx={{
												py: 1,
												px: 2,
												color: "text.primary",
												borderBottom: `1px solid ${theme.palette.divider}`
											}}
										>
											{log.metadata?.invoiceNumber ? (
												<Link
													href={`/invoice?invoiceNumber=${encodeURIComponent(log.metadata.invoiceNumber)}`}
													target="_blank"
													rel="noopener noreferrer"
													underline="hover"
													color="primary"
													style={{
														cursor: "pointer"
													}}
												>
													{log.metadata.invoiceNumber}
												</Link>
											) : (
												"-"
											)}
										</TableCell>
										<TableCell
											sx={{
												py: 1,
												px: 2,
												color: "text.primary",
												borderBottom: `1px solid ${theme.palette.divider}`
											}}
										>
											{log.operation}
										</TableCell>
										<TableCell
											sx={{
												py: 1,
												px: 2,
												color: "text.primary",
												borderBottom: `1px solid ${theme.palette.divider}`
											}}
										>
											{log.username}
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell
											colSpan={7}
											sx={{ p: 0, borderBottom: "none" }}
										>
											<Collapse
												in={isExpanded}
												timeout="auto"
												unmountOnExit
											>
												<Box
													sx={{
														p: 2,
														bgcolor: alpha(
															theme.palette
																.primary.main,
															isDarkMode
																? 0.08
																: 0.04
														),
														borderTop: `1px solid ${theme.palette.divider}`
													}}
												>
													<ChangeDetail
														auditLog={log}
													/>
												</Box>
											</Collapse>
										</TableCell>
									</TableRow>
								</React.Fragment>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
			<Box
				sx={{
					display: "flex",
					justifyContent: "flex-end",
					mt: 2
				}}
			>
				<Pagination
					page={page}
					count={pageCount}
					onChange={(_, value) => setPage(value)}
					color="primary"
					sx={{
						"& .MuiPaginationItem-root": {
							color: theme.palette.text.primary,
							borderColor: theme.palette.divider
						}
					}}
				/>
			</Box>
		</Paper>
	);
};
